import { Game } from './catalog';

export const STORE_TIER = {
  MERCHANT: 'merchant',
  RETAILER: 'retailer',
  CERTIFIED: 'certified',
} as const;

export type StoreTier = (typeof STORE_TIER)[keyof typeof STORE_TIER];

export const STORE_TIER_LABELS = {
  [STORE_TIER.MERCHANT]: 'Vendedor',
  [STORE_TIER.RETAILER]: 'Vendedor Certificado',
  [STORE_TIER.CERTIFIED]: 'Loja',
} as const;

export const STORE_MEDIA_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
] as const;

export const STORE_MEDIA_ERROR_CODE = {
  SIZE: 'store-image-too-large',
  DIMENSIONS: 'store-image-dimensions-exceeded',
  FORMAT: 'store-image-invalid-format',
  CORRUPT: 'store-image-unreadable',
  ANIMATED: 'store-image-animated',
  MISSING: 'store-image-required',
} as const;

export const STORE_MEDIA_KIND = {
  PROFILE: 'profile',
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
} as const;

export type StoreMediaKind =
  (typeof STORE_MEDIA_KIND)[keyof typeof STORE_MEDIA_KIND];

export const STORE_MEDIA_LIMITS = {
  [STORE_MEDIA_KIND.PROFILE]: {
    label: 'Foto da loja',
    width: 720,
    height: 720,
    megabytes: 1,
    allowedTiers: [
      STORE_TIER.MERCHANT,
      STORE_TIER.RETAILER,
      STORE_TIER.CERTIFIED,
    ] as StoreTier[],
  },
  [STORE_MEDIA_KIND.DESKTOP]: {
    label: 'Banner desktop',
    width: 1856,
    height: 232,
    megabytes: 5,
    allowedTiers: [STORE_TIER.RETAILER, STORE_TIER.CERTIFIED] as StoreTier[],
  },
  [STORE_MEDIA_KIND.MOBILE]: {
    label: 'Banner mobile',
    width: 384,
    height: 288,
    megabytes: 1,
    allowedTiers: [STORE_TIER.RETAILER, STORE_TIER.CERTIFIED] as StoreTier[],
  },
} as const;

export const STORE_SETTINGS_TAB = {
  INFORMATION: 'information',
  IMAGES: 'images',
  SHIPPING: 'shipping',
  DANGER: 'danger',
} as const;

export type StoreSettingsTab =
  (typeof STORE_SETTINGS_TAB)[keyof typeof STORE_SETTINGS_TAB];

export interface PublicStoreAddress {
  city: {
    id: string;
    name: string;
    state: { id: string; name: string; code: string };
  };
}

export interface StoreAddress extends PublicStoreAddress {
  postalCode?: string;
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city: PublicStoreAddress['city'] & {
    ibgeCode?: string;
    state: PublicStoreAddress['city']['state'] & { ibgeCode?: string };
  };
}

export interface PublicStore {
  tier?: StoreTier;
  bannerDesktopUrl?: string;
  bannerMobileUrl?: string;
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  description?: string;
  active: boolean;
  games: Game[];
  address?: PublicStoreAddress;
  openedAt?: string;
  completedSales: number;
  rating: { average: number; count: number };
}

export interface OwnerStore extends Omit<PublicStore, 'address'> {
  progress?: StoreProgress;
  documentCnpj?: string;
  corporateName?: string;
  stateRegistration?: string;
  freeShippingThreshold?: number;
  buyoutLimit?: number;
  shippingMethods: StoreShippingMethod[];
  address?: StoreAddress;
}

export interface AdminStore {
  tier?: StoreTier;
  retailerOverride?: boolean;
  id: string;
  name: string;
  slug: string;
  documentCnpj?: string;
  corporateName?: string;
  stateRegistration?: string;
  active: boolean;
  openedAt?: string;
}

export interface AdminStoresFilters {
  search?: string;
  status?: 'active' | 'inactive' | 'incomplete';
  page: number;
  limit: number;
}

export interface AdminStoresPage {
  items: AdminStore[];
  total: number;
  page: number;
  limit: number;
}

export type Store = PublicStore;

export const STORE_RANK = {
  COMMON: 'common',
  RARE: 'rare',
  SUPER: 'super',
  ULTRA: 'ultra',
  SECRET: 'secret',
  LEGENDARY: 'legendary',
} as const;

export type StoreRank = (typeof STORE_RANK)[keyof typeof STORE_RANK];

export interface StoreAchievement {
  code: string;
  label: string;
  description: string;
  featured: boolean;
  awardedAt: string;
}

export interface PublicStoreProgress {
  rank: StoreRank;
  label: string;
  achievements: StoreAchievement[];
}

export interface StoreProgress extends PublicStoreProgress {
  totalXp: number;
  currentRankMinXp: number;
  nextRank?: StoreRank;
  nextRankLabel?: string;
  nextRankXp?: number;
  xpRemaining: number;
  progressPercentage: number;
  hasUnseenPromotion: boolean;
  objectives: Array<{
    code: string;
    label: string;
    current: number;
    target: number;
    completed: boolean;
  }>;
}

export interface StoreInput {
  name: string;
  slug: string;
  logoUrl?: string;
  description?: string;
  documentCnpj?: string;
  corporateName?: string;
  stateRegistration?: string;
  freeShippingThreshold?: number;
  buyoutLimit?: number;
  shippingMethods: StoreShippingMethodInput[];
  gameIds: string[];
  address?: {
    postalCode: string;
    street: string;
    number: string;
    complement?: string;
    district: string;
    cityIbgeCode: string;
    city: string;
    stateCode: string;
  };
}

export const STORE_SHIPPING_METHOD = {
  REGISTERED_MAIL: 'registered_mail',
  CORREIOS: 'correios',
  LOCAL_STORE: 'local_store',
} as const;

export type StoreShippingMethodCode =
  (typeof STORE_SHIPPING_METHOD)[keyof typeof STORE_SHIPPING_METHOD];

export interface StoreShippingMethod {
  code: StoreShippingMethodCode;
  active: boolean;
  fixedPrice?: number;
  priceUpTo100?: number;
  priceOver100?: number;
  pickupName?: string;
}

export type StoreShippingMethodInput = StoreShippingMethod;
